import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SUPPLIER_TRADES,
  SUPPLIER_TRADE_LABELS,
  formatNzDate,
  streetLine,
  type SupplierTrade,
} from '@promanage/core';
import {
  MAINTENANCE_RESPONSE_TARGET_DAYS,
  MAINTENANCE_URGENCIES,
  MAINTENANCE_URGENCY_LABELS,
  MAINTENANCE_URGENCY_MEANS,
  MAX_MAINTENANCE_SUMMARY_LENGTH,
  maintenanceResponseDue,
  type MaintenanceUrgency,
} from '@promanage/core/maintenance';
import { Button, Field, Input, Panel, PanelHeader, Select, Textarea } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { useUnsavedChanges } from '../components/UnsavedChanges';
import { todayIso } from '../features/property/today';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/maintenance/new')({
  component: NewMaintenanceJobPage,
});

/**
 * Report a maintenance job.
 *
 * THIS FORM AUTHORISES NOTHING, and the panel on the right says so out loud. Taking the call is a
 * record of what somebody said; no contractor is sent and no money is committed until somebody
 * with a trust role approves a figure, which is a separate act on the job's own page. Saying that
 * here is what stops a property manager either hesitating over it or believing they have just
 * spent an owner's money.
 *
 * "REPORTED ON" DEFAULTS TO TODAY AND IS EDITABLE, which is the one field on this form worth an
 * argument. It is the day the tenant TOLD us, not the day it was keyed — a Friday-night call
 * entered on Monday morning has already used the weekend, and the service target counts from the
 * call. Defaulting it to today makes the common case one keystroke; leaving it editable is what
 * keeps the target honest on the uncommon one.
 *
 * THE TRADE IS THE `SUPPLIER_TRADES` VOCABULARY, not a second list of job categories. What the
 * tenant reports is the summary; the trade is the triage, and it is what makes "who do we call for
 * this" a filter over the Contractors list rather than a translation table.
 */
function NewMaintenanceJobPage() {
  const navigate = useNavigate();
  const unsaved = useUnsavedChanges();
  const queryClient = useQueryClient();
  const today = todayIso();

  const properties = useQuery(trpc.property.list.queryOptions(undefined, { staleTime: 30_000 }));
  const tenancies = useQuery(
    trpc.property.tenancies.queryOptions(undefined, { staleTime: 30_000 }),
  );

  const [propertyId, setPropertyId] = useState('');
  const [tenancyId, setTenancyId] = useState('');
  const [reportedByPersonId, setReportedByPersonId] = useState('');
  const [trade, setTrade] = useState<SupplierTrade>('plumber');
  const [urgency, setUrgency] = useState<MaintenanceUrgency>('routine');
  const [summary, setSummary] = useState('');
  const [details, setDetails] = useState('');
  const [accessNotes, setAccessNotes] = useState('');
  const [reportedOn, setReportedOn] = useState(today);

  const report = useMutation(
    trpc.maintenance.report.mutationOptions({
      meta: { errorTitle: 'Could not report that job' },
      onSuccess(created) {
        unsaved.saved();
        void queryClient.invalidateQueries({ queryKey: trpc.maintenance.list.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.maintenance.summary.queryKey() });
        // Straight to the record: the next thing anybody does with a reported job is get a price
        // or approve one, and that is the page both happen on.
        void navigate({ to: '/maintenance/$ref', params: { ref: created.ref } });
      },
    }),
  );

  const propertyOptions = useMemo(
    () =>
      (properties.data ?? [])
        .map((property) => ({
          id: property.id,
          ref: property.ref,
          label: `${streetLine(property)}, ${property.city}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [properties.data],
  );

  /*
   * ONLY THIS PROPERTY'S LETTINGS, and ended ones are included. A job reported the week after a
   * tenancy finished is usually the exit inspection's — and the record has to name the people who
   * were living there, not the ones who are.
   */
  const tenancyOptions = useMemo(
    () => (tenancies.data ?? []).filter((tenancy) => tenancy.propertyId === propertyId),
    [tenancies.data, propertyId],
  );

  const chosenTenancy = tenancyOptions.find((tenancy) => tenancy.id === tenancyId) ?? null;

  const dueOn = reportedOn === '' ? null : maintenanceResponseDue(reportedOn, urgency);

  const ready = propertyId !== '' && summary.trim() !== '' && reportedOn !== '';

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready) return;

    report.mutate({
      propertyId,
      trade,
      urgency,
      summary: summary.trim(),
      reportedOn,
      ...(tenancyId === '' ? {} : { tenancyId }),
      ...(reportedByPersonId === '' ? {} : { reportedByPersonId }),
      ...(details.trim() === '' ? {} : { details: details.trim() }),
      ...(accessNotes.trim() === '' ? {} : { accessNotes: accessNotes.trim() }),
    });
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Report a job"
        subtitle="One piece of work, on one property"
        actions={
          <Link to="/maintenance">
            <Button size="sm" variant="secondary">
              All jobs
            </Button>
          </Link>
        }
      />

      <form onChange={unsaved.touch} onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Panel className="p-5">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Property" required hint="Whose house it is">
              {(props) => (
                <Select
                  {...props}
                  value={propertyId}
                  onChange={(e) => {
                    setPropertyId(e.target.value);
                    // The tenancy and the person both belong to the old property. Keeping them
                    // would attach a letting on one house to a job on another, which the server
                    // refuses — better to clear them than to hand somebody a refusal.
                    setTenancyId('');
                    setReportedByPersonId('');
                  }}
                >
                  <option value="">Choose a property…</option>
                  {propertyOptions.map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.label} · {property.ref}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label="Tenancy" hint="Who was living there. Leave blank if vacant">
              {(props) => (
                <Select
                  {...props}
                  value={tenancyId}
                  disabled={propertyId === ''}
                  onChange={(e) => {
                    setTenancyId(e.target.value);
                    setReportedByPersonId('');
                  }}
                >
                  <option value="">No tenancy</option>
                  {tenancyOptions.map((tenancy) => (
                    <option key={tenancy.id} value={tenancy.id}>
                      {tenancy.ref} ·{' '}
                      {tenancy.people.map((p) => p.lastName).join(', ') || 'No one attached'}
                      {tenancy.endsOn === null ? '' : ' (ended)'}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label="Reported by" hint="Who rang. Leave blank if we found it ourselves">
              {(props) => (
                <Select
                  {...props}
                  value={reportedByPersonId}
                  disabled={chosenTenancy === null}
                  onChange={(e) => setReportedByPersonId(e.target.value)}
                >
                  <option value="">Not recorded</option>
                  {(chosenTenancy?.people ?? []).map((person) => (
                    <option key={person.personId} value={person.personId}>
                      {person.preferredName ?? person.firstName} {person.lastName}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
        </Panel>

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
            <Panel className="p-5">
              <PanelHeader title="What is wrong" className="p-0 pb-3" />

              <Field
                label="Summary"
                required
                hint={`One line, in the words it was reported (${String(MAX_MAINTENANCE_SUMMARY_LENGTH)} characters)`}
              >
                {(props) => (
                  <Input
                    {...props}
                    maxLength={MAX_MAINTENANCE_SUMMARY_LENGTH}
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    placeholder="No hot water — cylinder leaking into the hall cupboard"
                  />
                )}
              </Field>

              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <Field label="Trade" required hint="Who we would call">
                  {(props) => (
                    <Select
                      {...props}
                      value={trade}
                      onChange={(e) => setTrade(e.target.value as SupplierTrade)}
                    >
                      {[...SUPPLIER_TRADES]
                        .sort((a, b) =>
                          SUPPLIER_TRADE_LABELS[a].localeCompare(SUPPLIER_TRADE_LABELS[b]),
                        )
                        .map((value) => (
                          <option key={value} value={value}>
                            {SUPPLIER_TRADE_LABELS[value]}
                          </option>
                        ))}
                    </Select>
                  )}
                </Field>

                <Field label="Urgency" required hint={MAINTENANCE_URGENCY_MEANS[urgency]}>
                  {(props) => (
                    <Select
                      {...props}
                      value={urgency}
                      onChange={(e) => setUrgency(e.target.value as MaintenanceUrgency)}
                    >
                      {MAINTENANCE_URGENCIES.map((value) => (
                        <option key={value} value={value}>
                          {MAINTENANCE_URGENCY_LABELS[value]}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>

                <Field
                  label="Reported on"
                  required
                  hint="The day we were told, not the day it was keyed"
                >
                  {(props) => (
                    <Input
                      {...props}
                      type="date"
                      value={reportedOn}
                      onChange={(e) => setReportedOn(e.target.value)}
                    />
                  )}
                </Field>
              </div>

              <div className="mt-3 grid gap-3">
                <Field label="Detail" hint="Anything else the contractor needs to know">
                  {(props) => (
                    <Textarea
                      {...props}
                      rows={3}
                      value={details}
                      onChange={(e) => setDetails(e.target.value)}
                      placeholder="Tenant has turned the mains off at the toby. Water damage to the hall carpet."
                    />
                  )}
                </Field>

                <Field label="Access" hint="How a tradesperson gets in — this goes on the order">
                  {(props) => (
                    <Textarea
                      {...props}
                      rows={2}
                      value={accessNotes}
                      onChange={(e) => setAccessNotes(e.target.value)}
                      placeholder="Keys at the office. Dog in the back yard — ring first."
                    />
                  )}
                </Field>
              </div>
            </Panel>
          </div>

          <div className="flex flex-col gap-4">
            <Panel className="p-5">
              <PanelHeader title="Nothing is authorised yet" className="p-0 pb-3" />
              <p className="text-[0.8125rem] text-ink-muted">
                Reporting a job records it and nothing else. No contractor is sent and no money is
                committed until somebody with a trust role approves a figure — which is a separate
                act, on the job’s own page, and the one an owner is shown if they dispute the spend.
              </p>
            </Panel>

            <Panel className="p-5">
              <PanelHeader title="Our service target" className="p-0 pb-3" />
              {dueOn === null ? (
                <p className="text-[0.8125rem] text-ink-muted">
                  Planned work has no target — it is scheduled rather than chased.
                </p>
              ) : (
                <p className="text-[0.8125rem] text-ink-muted">
                  A contractor should be sent by{' '}
                  <strong className="text-ink">{formatNzDate(dueOn)}</strong> —{' '}
                  {String(MAINTENANCE_RESPONSE_TARGET_DAYS[urgency])} working days from the day it
                  was reported.
                </p>
              )}
              <p className="mt-3 text-[0.75rem] text-ink-subtle">
                This is the agency’s own target, not a legal deadline. The Residential Tenancies Act
                requires a landlord to act within a reasonable time and puts no number of days on
                it. The date skips weekends but not public holidays, so treat it as approximate.
              </p>
            </Panel>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button type="submit" size="lg" disabled={!ready || report.isPending}>
            {report.isPending ? 'Saving' : 'Report job'}
          </Button>
          <Link to="/maintenance">
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Link>
        </div>
      </form>
    </div>
  );
}
